import React from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { GripVertical, Clock, Bug, Lightbulb, MessageSquare, User, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { RichTextContent } from '../ui/RichTextEditor';
import {
  Ticket,
  TicketArea,
  ticketTypeLabels,
  ticketPriorityLabels,
  ticketPriorityColors,
  ticketAreaLabels
} from '../../types/ticket';
import { TaskArea } from '../../types/task';

// Map ticket area to task area for DnD compatibility
export const ticketAreaToTaskArea = (area: TicketArea): TaskArea => {
  if (area === 'qa') return 'Q&A';
  return area as TaskArea;
};

export const taskAreaToTicketArea = (area: TaskArea): TicketArea => {
  if (area === 'Q&A') return 'qa';
  return area as TicketArea;
};

// Shared component for rendering ticket card content
export const TicketCardContent: React.FC<{
  ticket: Ticket;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  isDragDisabled?: boolean;
  onEdit?: (ticket: Ticket) => void;
  onDelete?: (ticket: Ticket) => void;
}> = ({ ticket, provided, snapshot, isDragDisabled = false, onEdit, onDelete }) => {
  const TypeIcon = ticket.tipo === 'bug' ? Bug : Lightbulb;

  return (
    <Card
      variant="glass"
      className={`
        group w-full ticket-card
        ${snapshot.isDragging
          ? 'shadow-xl border-blue-500/50 bg-blue-500/20 backdrop-blur-sm'
          : 'hover:shadow-lg hover:shadow-white/5 hover:border-white/20'
        }
      `}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          <div
            {...provided.dragHandleProps}
            className={`
              flex-shrink-0 p-1 rounded
              hover:bg-white/10 transition-colors
              ${isDragDisabled
                ? 'opacity-30 cursor-not-allowed'
                : snapshot.isDragging
                  ? 'cursor-grabbing'
                  : 'cursor-grab'
              }
            `}
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4 text-foreground-tertiary" />
          </div>

          {/* Ticket Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              {/* Type icon + label */}
              <div className="flex items-center gap-1.5 mb-1">
                <TypeIcon
                  className={`w-4 h-4 flex-shrink-0 ${ticket.tipo === 'bug' ? 'text-red-400' : 'text-yellow-400'}`}
                />
                <span className="text-xs text-foreground-tertiary">
                  {ticket.tipo === 'bug' ? 'Bug' : 'Melhoria'}
                </span>
              </div>

              {/* Action buttons */}
              {(onEdit || onDelete) && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(ticket);
                      }}
                      className="h-6 w-6 p-0 text-foreground-tertiary hover:text-foreground-primary"
                      title="Editar chamado"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(ticket);
                      }}
                      className="h-6 w-6 p-0 text-foreground-tertiary hover:text-red-400"
                      title="Excluir chamado"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            <h4 className="font-medium text-foreground-primary text-sm leading-tight mb-1 line-clamp-2">
              {ticket.titulo}
            </h4>

            {ticket.descricao && (
              <div className="text-xs text-foreground-tertiary mb-2 line-clamp-2">
                <RichTextContent content={ticket.descricao} className="text-xs" />
              </div>
            )}

            {/* Badges */}
            <div className="flex flex-wrap gap-1 mb-2">
              <span
                className="px-1.5 py-0.5 text-xs font-medium rounded"
                style={{
                  backgroundColor: `${ticketPriorityColors[ticket.prioridade]}20`,
                  color: ticketPriorityColors[ticket.prioridade]
                }}
              >
                {ticketPriorityLabels[ticket.prioridade]}
              </span>
              <Badge className="bg-white/10 text-foreground-secondary text-xs px-1.5 py-0.5">
                {ticketAreaLabels[ticket.area]}
              </Badge>
            </div>

            {/* Footer info */}
            <div className="flex items-center gap-3 text-xs text-foreground-tertiary">
              {ticket.estimatedHours != null && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {ticket.estimatedHours}h
                </span>
              )}
              {ticket.responsavelId && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {typeof ticket.responsavelId === 'object' ? ticket.responsavelId.name : ''}
                </span>
              )}
              <span>
                {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
              </span>
              {ticket.comments && ticket.comments.length > 0 && (
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  {ticket.comments.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface DraggableTicketCardProps {
  ticket: Ticket;
  index: number;
  isDragDisabled?: boolean;
  onEdit?: (ticket: Ticket) => void;
  onDelete?: (ticket: Ticket) => void;
}

export const DraggableTicketCard: React.FC<DraggableTicketCardProps> = ({
  ticket,
  index,
  isDragDisabled = false,
  onEdit,
  onDelete
}) => {
  return (
    <Draggable draggableId={`ticket-${ticket._id}`} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="mb-2 w-full"
        >
          <TicketCardContent
            ticket={ticket}
            provided={provided}
            snapshot={snapshot}
            isDragDisabled={isDragDisabled}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      )}
    </Draggable>
  );
};

// Function to create renderClone for Droppable
export const createRenderTicketClone = (tickets: Ticket[]) => {
  return (provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: any) => {
    const ticketId = rubric.draggableId.replace('ticket-', '');
    const ticket = tickets.find(t => t._id === ticketId);
    if (!ticket) return null;

    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className="mb-2 w-80"
        style={{
          ...provided.draggableProps.style,
          width: '320px'
        }}
      >
        <TicketCardContent
          ticket={ticket}
          provided={provided}
          snapshot={snapshot}
          isDragDisabled={false}
        />
      </div>
    );
  };
};
